import { differenceInDays, parseISO, startOfDay } from "date-fns";

export interface ItineraryItemForExtraction {
  id: string;
  item_type: string | null;
  title: string | null;
  supplier: string | null;
  check_in?: string | null;
  check_out?: string | null;
  include_in_quote?: boolean;
  /** Explicit, per-item inclusion lines captured on the itinerary item */
  inclusions?: string[] | null;
  /** Explicit, per-item exclusion lines captured on the itinerary item */
  exclusions?: string[] | null;
}


export interface ExclusionOption {
  id: string;
  text: string;
  autoExcludeWhen?: 'no_flights' | 'no_insurance';
  isDefault?: boolean;
}

export const EXCLUSION_OPTIONS: ExclusionOption[] = [
  { id: 'flights_intl', text: 'International flights', isDefault: true },
  { id: 'flights_domestic', text: 'Domestic flights', isDefault: true },
  { id: 'visa', text: 'Visa fees and border taxes', isDefault: true },
  { id: 'insurance', text: 'Travel insurance', isDefault: true },
  { id: 'personal', text: 'Personal expenses', isDefault: true },
  { id: 'tips', text: 'Tips and gratuities', isDefault: true },
  { id: 'drinks', text: 'Drinks and beverages unless specified', isDefault: true },
  { id: 'laundry', text: 'Laundry services' },
  { id: 'phone', text: 'Phone calls and internet charges' },
  { id: 'optional', text: 'Optional activities not listed in itinerary' },
  { id: 'excess_baggage', text: 'Excess baggage fees' },
  { id: 'personal_nature', text: 'Items of a personal nature' },
];

export interface InclusionOption {
  id: string;
  text: string;
  isDefault?: boolean;
}

/**
 * High-level, programme-wide inclusions shown at the end of a quote.
 * These are deliberately short and infrastructural — per-item inclusions
 * live on the itinerary items themselves and render beneath each item.
 */
export const PROGRAMME_INCLUSION_OPTIONS: InclusionOption[] = [
  { id: 'vat', text: 'VAT at 15%', isDefault: true },
  { id: 'tourism_levy', text: 'Tourism levy', isDefault: true },
  { id: 'conservation', text: 'Conservation and park fees', isDefault: true },
  { id: 'transport', text: 'All transport as per programme', isDefault: true },
  { id: 'guiding', text: 'Guiding and ground handling', isDefault: true },
  { id: 'meals', text: 'Meals as specified in the programme' },
  { id: 'water', text: 'Bottled water on tour vehicles' },
  { id: 'entrance', text: 'Entrance fees as per programme' },
  { id: 'porterage', text: 'Porterage where applicable' },
  { id: 'accommodation', text: 'Accommodation as per programme' },
];

export function getDefaultProgrammeInclusions(): string[] {
  return PROGRAMME_INCLUSION_OPTIONS.filter(o => o.isDefault).map(o => o.text);
}

export function getDefaultProgrammeExclusions(): string[] {
  return EXCLUSION_OPTIONS.filter(o => o.isDefault).map(o => o.text);
}

function calculateNights(checkIn: string | null | undefined, checkOut: string | null | undefined): number {
  if (!checkIn || !checkOut) return 1;
  try {
    const start = startOfDay(parseISO(checkIn));
    const end = startOfDay(parseISO(checkOut));
    const nights = differenceInDays(end, start);
    return nights > 0 ? nights : 1;
  } catch {
    return 1;
  }
}

/** Clean, trimmed, non-empty lines from an item's explicit list */
function cleanLines(lines: string[] | null | undefined): string[] {
  return (lines || []).map(l => (l || "").trim()).filter(l => l.length > 0);
}

function hasExplicitInclusions(item: ItineraryItemForExtraction): boolean {
  return cleanLines(item.inclusions).length > 0;
}

/**
 * Explicit per-item exclusion lines, in itinerary order, de-duplicated.
 */
export function extractExclusionsFromItems(items: ItineraryItemForExtraction[]): string[] {
  const out: string[] = [];
  items
    .filter(i => i.include_in_quote !== false)
    .forEach(i => cleanLines(i.exclusions).forEach(l => out.push(l)));
  return Array.from(new Set(out));
}

export function extractInclusionsFromItems(items: ItineraryItemForExtraction[]): string[] {
  const inclusions: string[] = [];

  const allIncluded = items.filter(i => i.include_in_quote !== false);

  // Explicit per-item inclusions win — listed first, in itinerary order.
  allIncluded.filter(hasExplicitInclusions).forEach(item => {
    cleanLines(item.inclusions).forEach(line => inclusions.push(line));
  });

  // Everything else falls back to the type-based wording below.
  const includedItems = allIncluded.filter(i => !hasExplicitInclusions(i));

  
  // Group accommodations
  const accommodations = includedItems.filter(i => i.item_type === 'accommodation');
  accommodations.forEach(acc => {
    const nights = calculateNights(acc.check_in, acc.check_out);
    const name = acc.supplier || acc.title || 'Accommodation';
    inclusions.push(`${nights} night${nights !== 1 ? 's' : ''} accommodation at ${name}`);
  });
  
  // Count meals
  const meals = includedItems.filter(i => i.item_type === 'meal');
  if (meals.length > 0) {
    inclusions.push(`Meals as per itinerary (${meals.length} meal${meals.length !== 1 ? 's' : ''} included)`);
  }
  
  // Transfers
  const transfers = includedItems.filter(i => i.item_type === 'transfer');
  if (transfers.length > 0) {
    inclusions.push('All transfers as per itinerary');
  }
  
  // Activities
  const activities = includedItems.filter(i => i.item_type === 'activity' || i.item_type === 'tour');
  if (activities.length > 0) {
    inclusions.push('Activities and excursions as listed in itinerary');
  }

  // Language excursions — treat like activities but call out distinctly
  const languageExcursions = includedItems.filter(i => i.item_type === 'language_excursions');
  if (languageExcursions.length > 0) {
    inclusions.push('Language excursions as listed in itinerary');
  }
  
  // Flights
  const flights = includedItems.filter(i => i.item_type === 'flight');
  if (flights.length > 0) {
    inclusions.push('Flights as specified in itinerary');
  }
  
  // Insurance
  const insurance = includedItems.filter(i => i.item_type === 'insurance');
  if (insurance.length > 0) {
    inclusions.push('Travel insurance coverage');
  }
  
  // Custom items
  const customItems = includedItems.filter(i => i.item_type === 'custom');
  customItems.forEach(item => {
    if (item.title) {
      inclusions.push(item.title);
    }
  });

  // Supplier / universal_fee — surface by title so they don't disappear
  const otherTitled = includedItems.filter(
    i => i.item_type === 'supplier' || i.item_type === 'universal_fee'
  );
  otherTitled.forEach(item => {
    const label = item.title || item.supplier;
    if (label) inclusions.push(label);
  });

  // Final safety net: any other included item that has a usable label and
  // hasn't already been represented above (rare item_types, new types, etc.)
  const handledTypes = new Set([
    'accommodation', 'meal', 'transfer', 'activity', 'tour',
    'language_excursions', 'flight', 'insurance', 'custom',
    'supplier', 'universal_fee',
  ]);
  includedItems.forEach(item => {
    if (item.item_type && handledTypes.has(item.item_type)) return;
    const label = item.title || item.supplier;
    if (label) inclusions.push(label);
  });

  // De-duplicate while preserving order
  return Array.from(new Set(inclusions));
}

export function getDefaultExclusions(_items: ItineraryItemForExtraction[]): string[] {
  // No items pre-ticked — users select what they need manually
  return [];
}

export function formatInclusionsText(inclusions: string[]): string {
  return inclusions.map(i => `• ${i}`).join('\n');
}

export function formatExclusionsText(exclusions: string[]): string {
  return exclusions.map(e => `• ${e}`).join('\n');
}

export function parseInclusionsText(text: string): string[] {
  return text
    .split('\n')
    .map(line => line.replace(/^[•\-\*]\s*/, '').trim())
    .filter(line => line.length > 0);
}

export function parseExclusionsText(text: string): string[] {
  return text
    .split('\n')
    .map(line => line.replace(/^[•\-\*]\s*/, '').trim())
    .filter(line => line.length > 0);
}
